import React, {FC, useEffect, useRef, useState} from 'react'
import moment from 'moment'
import {addDays} from 'date-fns'
import AppDatePickerWithCustomOptions from '../../orders/components/AppDatePickerWithCustomOptions'

export type DashboardDateRange = {
  from_date: string
  to_date: string
  label: string
}

type Props = {
  value: DashboardDateRange
  onChange: (range: DashboardDateRange) => void
  onApply: () => void
  onReset?: () => void
  disabled?: boolean
  compact?: boolean
  showReset?: boolean
}

export const defaultDashboardDateRange = (): DashboardDateRange => ({
  from_date: moment(addDays(new Date(), -29)).utc(true).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
  to_date: moment(new Date()).utc(true).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
  label: 'Last 30 Days',
})

const DashboardDateFilter: FC<Props> = ({
  value,
  onChange,
  onApply,
  onReset,
  disabled = false,
  compact = false,
  showReset = true,
}) => {
  const [isDatePickerVisible, setIsDatePickerVisible] = useState(false)
  const dropdownRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDatePickerVisible(false)
      }
    }

    if (isDatePickerVisible) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isDatePickerVisible])

  return (
    <div className={`d-flex gap-2 ${compact ? 'flex-wrap' : ''}`}>
      <div className='position-relative' ref={dropdownRef}>
        <button
          type='button'
          className='btn btn-sm text-dark border d-flex align-items-center dashboard-date-btn'
          onClick={() => setIsDatePickerVisible(!isDatePickerVisible)}
          disabled={disabled}
        >
          <i className='bi bi-calendar dashboard-date-icon' />
          <span className='dashboard-date-label'>
            {value.label && value.label !== 'Custom'
              ? value.label
              : `${moment(value.from_date).format('YYYY/MM/DD')} - ${moment(value.to_date).format('YYYY/MM/DD')}`}
          </span>
        </button>

        <AppDatePickerWithCustomOptions
          isVisible={isDatePickerVisible}
          handleClose={() => setIsDatePickerVisible(false)}
          initialRange={{
            startDate: value.from_date,
            endDate: value.to_date,
            label: value.label,
          }}
          onPickRange={({startDate, endDate, label}) => {
            const diff = moment(endDate).startOf('day').diff(moment(startDate).startOf('day'), 'days')
            const isCustom = label === 'Custom' || diff > 30
            onChange({
              from_date: moment(startDate).format('YYYY-MM-DD'),
              to_date: moment(endDate).format('YYYY-MM-DD'),
              label: isCustom ? 'Custom' : label,
            })
          }}
        />
      </div>

      <button
        type='button'
        className='btn btn-sm btn-success fw-bold px-3 shadow-sm dashboard-filter-btn'
        onClick={onApply}
        disabled={disabled}
      >
        Apply
      </button>

      {showReset && onReset ? (
        <button
          type='button'
          className='btn btn-sm btn-warning fw-bold px-3 shadow-sm dashboard-filter-btn'
          onClick={onReset}
          disabled={disabled}
        >
          Reset
        </button>
      ) : null}
    </div>
  )
}

export default DashboardDateFilter
